import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useStore } from 'context/store-context';
import { GET_APP_EMBED_STATUS } from 'gql/queries';
import { Evidence, readThemeAppEmbedEvidence, themeAppEmbedMessage } from 'utils/themeAppEmbed';

type State = Evidence & { storeId: string | undefined; shopDomain?: string; expired: boolean };
const unavailable: Evidence = { status: 'unavailable', editorUrl: null };

/** Shared by Home and offering settings; an enabled embed never certifies a cart. */
export const useThemeAppEmbedStatus = () => {
  const { storeId, storeProperties } = useStore();
  const shopDomain = storeProperties?.id === storeId && typeof storeProperties?.store === 'string'
    ? storeProperties.store.toLowerCase() : undefined;
  const [query] = useLazyQuery(GET_APP_EMBED_STATUS, {
    fetchPolicy: 'no-cache', context: { queryDeduplication: false },
  });
  const [state, setState] = useState<State>({ ...unavailable, storeId: undefined, status: 'checking', expired: false });
  const generation = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deadline = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editorReturnPending = useRef(false);

  const stop = useCallback(() => {
    generation.current++;
    if (pollTimer.current !== null) clearTimeout(pollTimer.current);
    if (deadline.current !== null) clearTimeout(deadline.current);
    pollTimer.current = null;
    deadline.current = null;
  }, []);

  const check = useCallback((poll = false) => {
    stop();
    if (!storeId) return;
    const request = generation.current;
    setState({ ...unavailable, storeId, shopDomain, status: 'checking', expired: false });
    deadline.current = setTimeout(() => {
      if (request !== generation.current) return;
      stop();
      setState({ ...unavailable, storeId, shopDomain, expired: true });
    }, 30000);

    const read = async () => {
      let evidence = unavailable;
      try {
        const result = await query({ variables: { storeId } });
        const errors = (result as { errors?: unknown[] }).errors;
        if (!result.error && !errors?.length) evidence = readThemeAppEmbedEvidence(result.data?.getAppEmbedStatus, storeId, shopDomain);
      } catch {}
      if (request !== generation.current) return;
      setState({ ...evidence, storeId, shopDomain, expired: false });
      if (poll && evidence.status === 'disabled') {
        // The next read starts after settlement; the overall deadline still applies.
        pollTimer.current = setTimeout(read, 2000);
      } else stop();
    };
    void read();
  }, [query, shopDomain, stop, storeId]);

  useEffect(() => {
    check();
    return stop;
  }, [check, stop]);

  useEffect(() => {
    editorReturnPending.current = false;
    const onReturn = () => {
      if (!editorReturnPending.current) return;
      editorReturnPending.current = false;
      check();
    };
    window.addEventListener('focus', onReturn);
    return () => {
      editorReturnPending.current = false;
      window.removeEventListener('focus', onReturn);
    };
  }, [check]);

  const current = state.storeId === storeId && state.shopDomain === shopDomain;
  const connectionNeeded = storeProperties?.id === storeId && Boolean(storeProperties?.uninstalledAt);
  const status = connectionNeeded ? 'unavailable' : current ? state.status : 'checking';
  const editorUrl = !connectionNeeded && current && storeProperties?.id === storeId ? state.editorUrl : null;
  const openEditor = useCallback(() => {
    if (!editorUrl) return false;
    editorReturnPending.current = true;
    window.open(editorUrl, '_blank', 'noopener,noreferrer');
    check(true);
    return true;
  }, [check, editorUrl]);

  return { storeId, shopDomain, connectionNeeded, status, editorUrl, check, openEditor,
    message: connectionNeeded ? 'The store is disconnected. Review the existing ShipAid connection before activating the theme app embed.'
      : themeAppEmbedMessage(status, current && state.expired, current ? state.reasonCode : undefined) };
};
